/**
 * O que faz: busca follow-ups que as regras impediram de sair e o aviso de WhatsApp desconectado.
 * Onde usa: FollowupCampaignMetrics (quadro "Bloqueados pelas regras") e FollowupBlockedExecutionsDialog.
 * Conexões: TanStack Query, Supabase.
 * Tabelas: SELECT em follow_up_execucoes com join de leads e follow_up_campaigns.
 * Data: 06/10/2026
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Categorias mostradas na tela; o texto vem do motivo gravado pelo process-followup-campaigns.
export type MotivoBloqueioFollowup =
  | 'janela_24h'
  | 'atrasado'
  | 'equipe_assumiu'
  | 'etapa_bloqueada'
  | 'ja_comprou'
  | 'outro';

export const ROTULO_MOTIVO_BLOQUEIO: Record<MotivoBloqueioFollowup, string> = {
  janela_24h: 'Passou de 24h sem resposta',
  atrasado: 'Ficou atrasado na fila',
  equipe_assumiu: 'Atendente assumiu a conversa',
  etapa_bloqueada: 'Etapa do funil bloqueada',
  ja_comprou: 'Lead já comprou',
  outro: 'Outro motivo',
};

export interface FollowupBloqueado {
  id: string;
  leadId: string;
  nome: string | null;
  telefone: string | null;
  campaignName: string;
  motivo: MotivoBloqueioFollowup;
  detalhe: string;
  bloqueadoEm: string;
}

interface LinhaBloqueada {
  id: string;
  lead_id: string;
  erro_detalhes: string | null;
  updated_at: string;
  lead: { nome: string | null; telefone: string | null } | null;
  campaign: { nome: string | null } | null;
}

// Traduz o motivo técnico gravado no banco para uma categoria simples.
export const classificarMotivoBloqueio = (detalhe: string | null): MotivoBloqueioFollowup => {
  const texto = (detalhe ?? '').toLowerCase();
  if (texto.includes('sem responder') || texto.includes('nunca enviou')) return 'janela_24h';
  if (texto.includes('atrasada')) return 'atrasado';
  if (texto.includes('equipe enviou')) return 'equipe_assumiu';
  if (texto.includes('etapa do funil')) return 'etapa_bloqueada';
  if (texto.includes('venda registrada')) return 'ja_comprou';
  return 'outro';
};

export const mapearBloqueados = (linhas: LinhaBloqueada[]): FollowupBloqueado[] =>
  linhas.map((linha) => ({
    id: linha.id,
    leadId: linha.lead_id,
    nome: linha.lead?.nome ?? null,
    telefone: linha.lead?.telefone ?? null,
    campaignName: linha.campaign?.nome?.trim() || 'Campanha sem nome',
    motivo: classificarMotivoBloqueio(linha.erro_detalhes),
    detalhe: linha.erro_detalhes ?? '',
    bloqueadoEm: linha.updated_at,
  }));

interface OpcoesBloqueados {
  startDate: Date | null;
  endDate: Date | null;
  campaignIds: string[];
}

export const useFollowupBloqueados = ({ startDate, endDate, campaignIds }: OpcoesBloqueados) => {
  const startIso = startDate?.toISOString() ?? null;
  const endIso = endDate?.toISOString() ?? null;
  const ids = [...new Set(campaignIds)].sort();

  return useQuery({
    queryKey: ['followup-bloqueados', startIso, endIso, ids],
    enabled: ids.length > 0,
    queryFn: async (): Promise<FollowupBloqueado[]> => {
      // Tabela: follow_up_execucoes | Operação: SELECT.
      // Campos: motivo do cancelamento, data, nome/telefone do lead e nome da campanha.
      // Filtros: campanhas visíveis na métrica, status cancelado com motivo das regras
      // ("Expirado:" ou "Cancelado:") e cancelamento dentro do período.
      // Risco: mostra dados pessoais; RLS e a lista de campanhas da clínica isolam os tenants.
      let query = (supabase as any)
        .from('follow_up_execucoes')
        .select('id, lead_id, erro_detalhes, updated_at, lead:leads (nome, telefone), campaign:follow_up_campaigns (nome)')
        .in('campaign_id', ids)
        .eq('status', 'cancelado')
        // O asterisco é o curinga do like no PostgREST; os motivos das regras começam com essas palavras.
        .or('erro_detalhes.like.Expirado*,erro_detalhes.like.Cancelado*')
        .order('updated_at', { ascending: false })
        .limit(500);

      if (startIso) query = query.gte('updated_at', startIso);
      if (endIso) query = query.lte('updated_at', endIso);

      const { data, error } = await query;
      if (error) throw new Error(`Erro ao carregar follow-ups bloqueados: ${error.message}`);
      return mapearBloqueados((data ?? []) as LinhaBloqueada[]);
    },
    staleTime: 30 * 1000,
  });
};

// Indica se o processor registrou WhatsApp desconectado nos últimos 30 minutos.
export const useFollowupWhatsappDesconectado = (campaignIds: string[]) => {
  const ids = [...new Set(campaignIds)].sort();

  return useQuery({
    queryKey: ['followup-whatsapp-desconectado', ids],
    enabled: ids.length > 0,
    queryFn: async (): Promise<string | null> => {
      const limite = new Date(Date.now() - 30 * 60_000).toISOString();
      // Tabela: follow_up_execucoes | Operação: SELECT.
      // Campos: ultimo_erro_em da execução mais recente com erro de desconexão.
      // Filtros: campanhas visíveis, status pendente, motivo "WhatsApp desconectado" e erro nos últimos 30 min.
      // Risco: somente leitura; não expõe conteúdo de mensagem.
      const { data, error } = await (supabase as any)
        .from('follow_up_execucoes')
        .select('ultimo_erro_em')
        .in('campaign_id', ids)
        .eq('status', 'pendente')
        .like('erro_detalhes', 'WhatsApp desconectado%')
        .gte('ultimo_erro_em', limite)
        .order('ultimo_erro_em', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw new Error(`Erro ao verificar conexão do WhatsApp: ${error.message}`);
      if (!data?.ultimo_erro_em) return null;

      // Tabela: follow_up_execucoes | Operação: SELECT | Campos: data_enviado.
      // Filtros: campanhas visíveis, status enviado, envio depois da última desconexão.
      // Motivo: se algo saiu depois do erro, a conexão voltou e o aviso some na hora.
      const { data: envioDepois, error: envioErro } = await (supabase as any)
        .from('follow_up_execucoes')
        .select('id')
        .in('campaign_id', ids)
        .eq('status', 'enviado')
        .gt('data_enviado', data.ultimo_erro_em)
        .limit(1)
        .maybeSingle();
      if (envioErro) throw new Error(`Erro ao verificar conexão do WhatsApp: ${envioErro.message}`);
      return envioDepois ? null : data.ultimo_erro_em;
    },
    // Reconsulta a cada minuto para o aviso sumir sozinho quando a conexão voltar.
    refetchInterval: 60 * 1000,
  });
};
